import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { Pause, Play } from 'lucide-react';

export default function OperationVideo({ src, srcWebm, poster, caption, alt, testid = 'operation-video' }) {
  const videoRef = useRef(null);
  const reduce = useReducedMotion();
  const [failed, setFailed] = useState(false);
  const [srcIdx, setSrcIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const sources = [src, srcWebm].filter(Boolean);
  const showVideo = !reduce && !failed;

  useEffect(() => {
    if (reduce && videoRef.current) {
      videoRef.current.pause();
      setPlaying(false);
    }
  }, [reduce]);

  useEffect(() => {
    const v = videoRef.current;
    if (v && playing && v.paused) {
      v.play().catch(() => {});
    }
  }, [srcIdx, playing]);

  const onSourceError = () => {
    if (srcIdx < sources.length - 1) setSrcIdx(srcIdx + 1);
    else setFailed(true);
  };

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <figure className="relative border border-line" data-testid={testid}>
      {showVideo ? (
        <video
          ref={videoRef}
          src={sources[srcIdx]}
          poster={poster}
          className="block h-auto w-full"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onError={onSourceError}
          aria-label={alt}
          data-testid={`${testid}-player`}
        />
      ) : (
        <img src={poster} alt={alt} className="block h-auto w-full" />
      )}
      {showVideo && (
        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? 'Pause operation video' : 'Play operation video'}
          aria-pressed={!playing}
          data-testid={`${testid}-play-pause`}
          className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center border border-line bg-base/60 text-white backdrop-blur transition-colors hover:border-silver"
        >
          {playing ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
        </button>
      )}
      {caption && (
        <figcaption className="border-t border-line px-6 py-4 font-mono text-[9px] uppercase tracking-[0.2em] text-mute sm:text-[10px]">
          {caption}
        </figcaption>
      )}
      <p className="sr-only">{alt}</p>
    </figure>
  );
}
